<script>
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import Icon from "../Icon.vue";

export default {
  components: {Icon, LoadingSpinner },
  props: {
    loaded: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    titleClass: {
      type: String,
      default: "text-lg font-semibold text-gray-900"
    },
    additionalText: {
      type: String,
      default: ""
    },
    additionalTextClass: {
      type: String,
      default: ""
    },
    leftSidebar: {
      type: Boolean,
      default: false
    },
    cardWidthClass: {
      type: String,
      default: ""
    },
    bordered: {
      type: Boolean,
      default: false
    },
    borderClass: {
      type: String,
      default: "border border-gray-200 rounded-md shadow-sm"
    },
    /** String name of the icon to display next to the title **/
    titleIcon: {
      type: String,
      required: false
    },
    /** Css color of title icon **/
    iconColor: {
      type: String,
      default: "#08602d"
    },
    /**
     * Array of objects of format:
     * {
     *   label: string,
     *   value: number / string
     * }
     */
    leftSidebarData: {
      type: Array,
      default: () => []
    },
    /** Prevents column flip at tablet breakpoint — keeps row layout.
     *  'always' = never flips to column
     *  'tablet' = stays row until 768px, then flips to column
     */
    compact: {
      type: [Boolean, String],
      default: false
    }
  },
  setup() {
    return {};
  },
  computed: {},
};
</script>

<template>
  <div :class="['base-container data-card-container', cardWidthClass, bordered ? borderClass : '', compact ? 'data-card-compact' : '', compact === 'tablet' ? 'data-card-compact-tablet' : '']">
    <div
      v-if="leftSidebar"
      class="left-sidebar"
    >
      <header>
        <div class="flex flex-row">
          <icon
            v-if="titleIcon"
            :icon="titleIcon"
            size="36px"
            :color="iconColor"
            class="mr-1 mt-1"
          />
          <div>
            <div class="page-title w-max">
              {{ title }}
              <slot name="leftSidebarExtras" />
            </div>
            <p
              v-if="additionalText"
              class="additional-text font-bold"
            >
              {{ additionalText }}
            </p>
          </div>
        </div>
      </header>
      <div
        class="left-sidebar-data-container"
      >
        <div
          v-for="(data, index) in leftSidebarData"
          :key="index"
        >
          <p class="additional-text">
            {{ data.label }}
          </p>
          <p class="text-2xl text-default_primary font-bold">
            {{ data.value }}
          </p>
        </div>
      </div>
      <slot name="leftSidebar" />
    </div>
    <div :class="{'main-content': true, 'left-sidebar-show': leftSidebar, 'loading': !loaded}">
      <header v-if="!leftSidebar && (title || additionalText)">
        <div class="flex flex-row items-center">
          <icon
            v-if="titleIcon"
            :icon="titleIcon"
            size="36px"
            :color="iconColor"
            class="mr-1"
          />
          <div>
            <div :class="['page-title', titleClass]">
              {{ title }}
              <slot name="titleExtras" />
            </div>
            <p
              v-if="additionalText && loaded"
              :class="['additional-text font-bold', additionalTextClass]"
            >
              {{ additionalText }}
            </p>
          </div>
        </div>
      </header>
      <template v-if="loaded">
        <slot />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.data-card-container {
  overflow: visible;
  display: flex;
  align-items: stretch;

  .left-sidebar {
    box-sizing: border-box;
    width: 180px;
    padding-right: 16px;

    .emphasized-text {
      padding-top: 8px;
    }
    .left-sidebar-data-container > div:not(:last-of-type) {
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(8, 18, 37, 0.12);
      margin-bottom: 8px;
      margin-bottom: 8px;
    }
  }
  .main-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    :deep(canvas) {
      align-self: center;
    }
    &.left-sidebar-show {
      width: calc(100% - 180px);
      padding-left: 30px;
    }
    .loading-spinner {
      flex-grow: 1;
    }
    :deep(.no-data-message) {
      flex-grow: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      @apply text-gray-500;
    }
  }
  header {
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(8, 18, 37, 0.12);
    margin-bottom: 8px;
  }
}

@media screen and (max-width: 1366px) {
  .data-card-container {
    .left-sidebar {
      width: 140px;
      font-size: 0.8rem;
      padding-right: 10px;
    }
    .main-content.left-sidebar-show {
      width: calc(100% - 140px);
      padding-left: 16px;
    }
  }
}

@media screen and (max-width: 1200px) {
  .data-card-container:not(.data-card-compact) {
    flex-direction: column;

    .left-sidebar {
      width: 100%;
      padding-right: 0;
      border-right: unset;
      margin-right: 0;
      padding-bottom: 12px;
      border-bottom: 1px solid rgba(8, 18, 37, 0.12);
      margin-bottom: 12px;

      .left-sidebar-data-container {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;

        > div:not(:last-of-type) {
          padding-bottom: 0;
          border-bottom: none;
          margin-bottom: 0;
          padding-right: 16px;
          border-right: 1px solid rgba(8, 18, 37, 0.12);
        }
      }
    }
    .main-content.left-sidebar-show {
      width: 100%;
    }
  }

  .data-card-compact {
    .left-sidebar {
      width: 120px;
      padding-right: 8px;
    }
    .main-content.left-sidebar-show {
      width: calc(100% - 120px);
      padding-left: 8px;
    }
  }
}

@media screen and (max-width: 768px) {
  .data-card-compact-tablet {
    flex-direction: column;

    .left-sidebar {
      width: 100%;
      padding-right: 0;
      padding-bottom: 12px;
      border-bottom: 1px solid rgba(8, 18, 37, 0.12);
      margin-bottom: 12px;

      .left-sidebar-data-container {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;

        > div:not(:last-of-type) {
          padding-bottom: 0;
          border-bottom: none;
          margin-bottom: 0;
          padding-right: 16px;
          border-right: 1px solid rgba(8, 18, 37, 0.12);
        }
      }
    }
    .main-content.left-sidebar-show {
      width: 100%;
      padding-left: 0;
    }
  }
}

//Print view adjustments
@media print {
  .data-card-container {
    display: flex !important;
    overflow: visible !important;
    width: 100% !important;
  }
  .main-content {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    overflow: visible !important;
  }
}
</style>
